import { supabase } from './supabase';
import type {
  DatabaseClient,
  DatabaseService,
  DatabaseProject,
  DatabaseTestimonial,
  DatabaseProjectDiscipline,
  DatabaseProjectRole,
  DatabaseProjectTool,
  DatabaseProjectColor,
  DatabaseProjectTypography,
  DatabaseProjectKPI,
  DatabaseProjectPress,
  DatabaseProjectGallery,
  DatabaseProjectBeforeAfter,
  DatabaseServiceDeliverable,
  DatabaseServiceProcess
} from './supabase';

// =====================================================
// TIPOS DE RETORNO PARA A APLICAÇÃO
// =====================================================

export interface Project {
  id: string;
  title: string;
  slug: string;
  client: string;
  clientId?: string;
  year: number;
  coverImage: string;
  disciplines: string[];
  roles: string[];
  tools: string[];
  summary: string;
  colors: string[];
  typography: string[];
  kpis: { label: string; value: string }[];
  press?: { outlet: string; url: string }[];
  gallery: { src: string; alt: string; caption?: string }[];
  beforeAfter?: { before: string; after: string; alt: string };
  featured: boolean;
  visible: boolean;
  auto_scroll?: boolean;
  cover_position?: 'top' | 'center' | 'bottom';
}

export interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string;
  quote: string;
  avatar: string;
  projectId?: string;
}

export interface Client {
  id: string;
  name: string;
  logo: string;
  url?: string;
  description?: string;
}

export interface Service {
  id: string;
  name: string;
  summary: string;
  deliverables: string[];
  process: string[];
  startingPrice?: string;
}

// =====================================================
// SERVIÇOS DE CLIENTES
// =====================================================

export const clientsApi = {
  async getAll(): Promise<Client[]> {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Client | null> {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(client: Omit<Client, 'id'>): Promise<Client> {
    const { data, error } = await supabase
      .from('clients')
      .insert([client])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: Partial<Client>): Promise<Client> {
    const { data, error } = await supabase
      .from('clients')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// =====================================================
// SERVIÇOS DE SERVIÇOS
// =====================================================

export const servicesApi = {
  async getAll(): Promise<Service[]> {
    const { data: servicesData, error: servicesError } = await supabase
      .from('services')
      .select('*')
      .order('name');

    if (servicesError) throw servicesError;

    const services = await Promise.all(
      (servicesData || []).map(async (service) => {
        // Buscar entregas
        const { data: deliverables } = await supabase
          .from('service_deliverables')
          .select('deliverable')
          .eq('service_id', service.id)
          .order('created_at');

        // Buscar processo
        const { data: process } = await supabase
          .from('service_process')
          .select('step')
          .eq('service_id', service.id)
          .order('order_index');

        return {
          id: service.id,
          name: service.name,
          summary: service.summary,
          startingPrice: service.starting_price,
          deliverables: deliverables?.map(d => d.deliverable) || [],
          process: process?.map(p => p.step) || []
        };
      })
    );

    return services;
  },

  async getById(id: string): Promise<Service | null> {
    const { data: service, error: serviceError } = await supabase
      .from('services')
      .select('*')
      .eq('id', id)
      .single();

    if (serviceError) throw serviceError;
    if (!service) return null;

    // Buscar entregas
    const { data: deliverables } = await supabase
      .from('service_deliverables')
      .select('deliverable')
      .eq('service_id', id)
      .order('created_at');

    // Buscar processo
    const { data: process } = await supabase
      .from('service_process')
      .select('step')
      .eq('service_id', id)
      .order('order_index');

    return {
      id: service.id,
      name: service.name,
      summary: service.summary,
      startingPrice: service.starting_price,
      deliverables: deliverables?.map(d => d.deliverable) || [],
      process: process?.map(p => p.step) || []
    };
  },

  async create(service: Omit<Service, 'id'>, deliverables: string[], process: string[]): Promise<Service> {
    // Inserir serviço
    const { data: newService, error: serviceError } = await supabase
      .from('services')
      .insert([{
        name: service.name,
        summary: service.summary,
        starting_price: service.startingPrice
      }])
      .select()
      .single();

    if (serviceError) throw serviceError;

    // Inserir entregas
    if (deliverables.length > 0) {
      const deliverablesData = deliverables.map(deliverable => ({
        service_id: newService.id,
        deliverable
      }));

      const { error: deliverablesError } = await supabase
        .from('service_deliverables')
        .insert(deliverablesData);

      if (deliverablesError) throw deliverablesError;
    }

    // Inserir processo
    if (process.length > 0) {
      const processData = process.map((step, index) => ({
        service_id: newService.id,
        step,
        order_index: index + 1
      }));

      const { error: processError } = await supabase
        .from('service_process')
        .insert(processData);

      if (processError) throw processError;
    }

    return {
      id: newService.id,
      name: newService.name,
      summary: newService.summary,
      startingPrice: newService.starting_price,
      deliverables,
      process
    };
  },

  async update(id: string, updates: Partial<Service>, deliverables?: string[], process?: string[]): Promise<Service> {
    // Atualizar serviço
    const { data: updatedService, error: serviceError } = await supabase
      .from('services')
      .update({
        name: updates.name,
        summary: updates.summary,
        starting_price: updates.startingPrice
      })
      .eq('id', id)
      .select()
      .single();

    if (serviceError) throw serviceError;

    // Atualizar entregas se fornecidas
    if (deliverables) {
      // Deletar entregas existentes
      await supabase
        .from('service_deliverables')
        .delete()
        .eq('service_id', id);

      // Inserir novas entregas
      if (deliverables.length > 0) {
        const deliverablesData = deliverables.map(deliverable => ({
          service_id: id,
          deliverable
        }));

        const { error: deliverablesError } = await supabase
          .from('service_deliverables')
          .insert(deliverablesData);

        if (deliverablesError) throw deliverablesError;
      }
    }

    // Atualizar processo se fornecido
    if (process) {
      // Deletar processo existente
      await supabase
        .from('service_process')
        .delete()
        .eq('service_id', id);

      // Inserir novo processo
      if (process.length > 0) {
        const processData = process.map((step, index) => ({
          service_id: id,
          step,
          order_index: index + 1
        }));

        const { error: processError } = await supabase
          .from('service_process')
          .insert(processData);

        if (processError) throw processError;
      }
    }

    // Buscar dados atualizados
    return this.getById(id) as Promise<Service>;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('services')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// =====================================================
// SERVIÇOS DE PROJETOS
// =====================================================

export const projectsApi = {
  async getAll(): Promise<Project[]> {
    const { data: projectsData, error: projectsError } = await supabase
      .from('projects')
      .select(`
        *,
        clients(name)
      `)
      .order('created_at', { ascending: false });

    if (projectsError) throw projectsError;

    const projects = await Promise.all(
      (projectsData || []).map(async (project) => {
        const projectId = project.id;

        // Buscar disciplinas
        const { data: disciplines } = await supabase
          .from('project_disciplines')
          .select('discipline')
          .eq('project_id', projectId);

        // Buscar papéis
        const { data: roles } = await supabase
          .from('project_roles')
          .select('role')
          .eq('project_id', projectId);

        // Buscar ferramentas
        const { data: tools } = await supabase
          .from('project_tools')
          .select('tool')
          .eq('project_id', projectId);

        // Buscar cores
        const { data: colors } = await supabase
          .from('project_colors')
          .select('color')
          .eq('project_id', projectId);

        // Buscar tipografia
        const { data: typography } = await supabase
          .from('project_typography')
          .select('font')
          .eq('project_id', projectId);

        // Buscar KPIs
        const { data: kpis } = await supabase
          .from('project_kpis')
          .select('label, value')
          .eq('project_id', projectId);

        // Buscar imprensa
        const { data: press } = await supabase
          .from('project_press')
          .select('outlet, url')
          .eq('project_id', projectId);

        // Buscar galeria
        const { data: gallery } = await supabase
          .from('project_gallery')
          .select('src, alt, caption')
          .eq('project_id', projectId);

        // Buscar before/after (usa maybeSingle para evitar erro 406 quando não há registro)
        const { data: beforeAfter } = await supabase
          .from('project_before_after')
          .select('before_image, after_image, alt_text')
          .eq('project_id', projectId)
          .maybeSingle();

        return {
          id: project.id,
          title: project.title,
          slug: project.slug,
          client: project.clients?.name || 'Cliente não especificado',
          clientId: project.client_id,
          year: project.year,
          coverImage: project.cover_image,
          disciplines: disciplines?.map(d => d.discipline) || [],
          roles: roles?.map(r => r.role) || [],
          tools: tools?.map(t => t.tool) || [],
          summary: project.summary,
          colors: colors?.map(c => c.color) || [],
          typography: typography?.map(t => t.font) || [],
          kpis: kpis?.map(k => ({ label: k.label, value: k.value })) || [],
          press: press?.map(p => ({ outlet: p.outlet, url: p.url })) || [],
          gallery: gallery?.map(g => ({ src: g.src, alt: g.alt, caption: g.caption })) || [],
          beforeAfter: beforeAfter ? {
            before: beforeAfter.before_image,
            after: beforeAfter.after_image,
            alt: beforeAfter.alt_text
          } : undefined,
          featured: project.featured,
          visible: project.visible !== false,
          auto_scroll: project.auto_scroll || false,
          cover_position: project.cover_position || 'top'
        };
      })
    );

    return projects;
  },

  async getBySlug(slug: string): Promise<Project | null> {
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select(`
        *,
        clients(name)
      `)
      .eq('slug', slug)
      .single();

    if (projectError) throw projectError;
    if (!project) return null;

    const projectId = project.id;

    // Buscar todos os dados relacionados (mesmo processo do getAll)
    const [disciplines, roles, tools, colors, typography, kpis, press, gallery, beforeAfter] = await Promise.all([
      supabase.from('project_disciplines').select('discipline').eq('project_id', projectId),
      supabase.from('project_roles').select('role').eq('project_id', projectId),
      supabase.from('project_tools').select('tool').eq('project_id', projectId),
      supabase.from('project_colors').select('color').eq('project_id', projectId),
      supabase.from('project_typography').select('font').eq('project_id', projectId),
      supabase.from('project_kpis').select('label, value').eq('project_id', projectId),
      supabase.from('project_press').select('outlet, url').eq('project_id', projectId),
      supabase.from('project_gallery').select('src, alt, caption').eq('project_id', projectId),
      supabase.from('project_before_after').select('before_image, after_image, alt_text').eq('project_id', projectId).maybeSingle()
    ]);

    return {
      id: project.id,
      title: project.title,
      slug: project.slug,
      client: project.clients?.name || 'Cliente não especificado',
      clientId: project.client_id,
      year: project.year,
      coverImage: project.cover_image,
      disciplines: disciplines.data?.map(d => d.discipline) || [],
      roles: roles.data?.map(r => r.role) || [],
      tools: tools.data?.map(t => t.tool) || [],
      summary: project.summary,
      colors: colors.data?.map(c => c.color) || [],
      typography: typography.data?.map(t => t.font) || [],
      kpis: kpis.data?.map(k => ({ label: k.label, value: k.value })) || [],
      press: press.data?.map(p => ({ outlet: p.outlet, url: p.url })) || [],
      gallery: gallery.data?.map(g => ({ src: g.src, alt: g.alt, caption: g.caption })) || [],
      beforeAfter: beforeAfter.data ? {
        before: beforeAfter.data.before_image,
        after: beforeAfter.data.after_image,
        alt: beforeAfter.data.alt_text
      } : undefined,
      featured: project.featured,
      visible: project.visible !== false,
      auto_scroll: project.auto_scroll || false,
      cover_position: project.cover_position || 'top'
    };
  },

  async getById(id: string): Promise<Project | null> {
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select(`
        *,
        clients(name)
      `)
      .eq('id', id)
      .single();

    if (projectError) throw projectError;
    if (!project) return null;

    const projectId = project.id;

    // Buscar todos os dados relacionados
    const [disciplines, roles, tools, colors, typography, kpis, press, gallery, beforeAfter] = await Promise.all([
      supabase.from('project_disciplines').select('discipline').eq('project_id', projectId),
      supabase.from('project_roles').select('role').eq('project_id', projectId),
      supabase.from('project_tools').select('tool').eq('project_id', projectId),
      supabase.from('project_colors').select('color').eq('project_id', projectId),
      supabase.from('project_typography').select('font').eq('project_id', projectId),
      supabase.from('project_kpis').select('label, value').eq('project_id', projectId),
      supabase.from('project_press').select('outlet, url').eq('project_id', projectId),
      supabase.from('project_gallery').select('src, alt, caption').eq('project_id', projectId),
      supabase.from('project_before_after').select('before_image, after_image, alt_text').eq('project_id', projectId).maybeSingle()
    ]);

    return {
      id: project.id,
      title: project.title,
      slug: project.slug,
      client: project.clients?.name || 'Cliente não especificado',
      clientId: project.client_id,
      year: project.year,
      coverImage: project.cover_image,
      disciplines: disciplines.data?.map(d => d.discipline) || [],
      roles: roles.data?.map(r => r.role) || [],
      tools: tools.data?.map(t => t.tool) || [],
      summary: project.summary,
      colors: colors.data?.map(c => c.color) || [],
      typography: typography.data?.map(t => t.font) || [],
      kpis: kpis.data?.map(k => ({ label: k.label, value: k.value })) || [],
      press: press.data?.map(p => ({ outlet: p.outlet, url: p.url })) || [],
      gallery: gallery.data?.map(g => ({ src: g.src, alt: g.alt, caption: g.caption })) || [],
      beforeAfter: beforeAfter.data ? {
        before: beforeAfter.data.before_image,
        after: beforeAfter.data.after_image,
        alt: beforeAfter.data.alt_text
      } : undefined,
      featured: project.featured,
      visible: project.visible !== false,
      auto_scroll: project.auto_scroll || false,
      cover_position: project.cover_position || 'top'
    };
  },

  async getFeatured(): Promise<Project[]> {
    const { data: projectsData, error: projectsError } = await supabase
      .from('projects')
      .select(`
        *,
        clients(name)
      `)
      .eq('featured', true)
      .order('created_at', { ascending: false });

    if (projectsError) throw projectsError;

    // Processar projetos com todos os dados relacionados
    const projects = await Promise.all(
      (projectsData || []).map(async (project) => {
        const projectId = project.id;

        // Buscar todos os dados relacionados em paralelo
        const [disciplines, roles, tools, colors, typography, kpis, press, gallery, beforeAfter] = await Promise.all([
          supabase.from('project_disciplines').select('discipline').eq('project_id', projectId),
          supabase.from('project_roles').select('role').eq('project_id', projectId),
          supabase.from('project_tools').select('tool').eq('project_id', projectId),
          supabase.from('project_colors').select('color').eq('project_id', projectId),
          supabase.from('project_typography').select('font').eq('project_id', projectId),
          supabase.from('project_kpis').select('label, value').eq('project_id', projectId),
          supabase.from('project_press').select('outlet, url').eq('project_id', projectId),
          supabase.from('project_gallery').select('src, alt, caption').eq('project_id', projectId),
          supabase.from('project_before_after').select('before_image, after_image, alt_text').eq('project_id', projectId).maybeSingle()
        ]);

        return {
          id: project.id,
          title: project.title,
          slug: project.slug,
          client: project.clients?.name || 'Cliente não especificado',
          clientId: project.client_id,
          year: project.year,
          coverImage: project.cover_image,
          disciplines: disciplines.data?.map(d => d.discipline) || [],
          roles: roles.data?.map(r => r.role) || [],
          tools: tools.data?.map(t => t.tool) || [],
          summary: project.summary,
          colors: colors.data?.map(c => c.color) || [],
          typography: typography.data?.map(t => t.font) || [],
          kpis: kpis.data?.map(k => ({ label: k.label, value: k.value })) || [],
          press: press.data?.map(p => ({ outlet: p.outlet, url: p.url })) || [],
          gallery: gallery.data?.map(g => ({ src: g.src, alt: g.alt, caption: g.caption })) || [],
          beforeAfter: beforeAfter.data ? {
            before: beforeAfter.data.before_image,
            after: beforeAfter.data.after_image,
            alt: beforeAfter.data.alt_text
          } : undefined,
          featured: project.featured,
          visible: project.visible !== false,
          auto_scroll: project.auto_scroll || false,
          cover_position: project.cover_position || 'top'
        };
      })
    );

    return projects;
  },

  async create(project: Omit<Project, 'id'>, clientId?: string): Promise<Project> {
    // Inserir projeto
    const { data: newProject, error: projectError } = await supabase
      .from('projects')
      .insert([{
        title: project.title,
        slug: project.slug,
        client_id: clientId,
        year: project.year,
        cover_image: project.coverImage,
        summary: project.summary,
        featured: project.featured,
        visible: project.visible,
        auto_scroll: project.auto_scroll || false,
        cover_position: project.cover_position || 'top'
      }])
      .select()
      .single();

    if (projectError) throw projectError;

    // Inserir dados relacionados
    await this.insertProjectRelations(newProject.id, project);

    return { ...project, id: newProject.id };
  },

  async update(id: string, updates: Partial<Project>): Promise<Project> {
    // Atualizar projeto
    const { data: updatedProject, error: projectError } = await supabase
      .from('projects')
      .update({
        title: updates.title,
        slug: updates.slug,
        year: updates.year,
        cover_image: updates.coverImage,
        summary: updates.summary,
        featured: updates.featured,
        visible: updates.visible,
        auto_scroll: updates.auto_scroll || false,
        cover_position: updates.cover_position || 'top'
      })
      .eq('id', id)
      .select()
      .single();

    if (projectError) throw projectError;

    // Se houver dados relacionados para atualizar, fazer isso
    if (updates.disciplines || updates.roles || updates.tools || updates.colors || updates.typography || updates.kpis || updates.press || updates.gallery) {
      // Deletar dados existentes e inserir novos
      await this.deleteProjectRelations(id);
      if (updatedProject) {
        await this.insertProjectRelations(id, updates as Project);
      }
    }

    // Retornar projeto atualizado
    return this.getBySlug(updatedProject.slug) as Promise<Project>;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async insertProjectRelations(projectId: string, project: Partial<Project>): Promise<void> {
    // Inserir disciplinas
    if (project.disciplines?.length) {
      const disciplinesData = project.disciplines.map(discipline => ({
        project_id: projectId,
        discipline
      }));
      await supabase.from('project_disciplines').insert(disciplinesData);
    }

    // Inserir papéis
    if (project.roles?.length) {
      const rolesData = project.roles.map(role => ({
        project_id: projectId,
        role
      }));
      await supabase.from('project_roles').insert(rolesData);
    }

    // Inserir ferramentas
    if (project.tools?.length) {
      const toolsData = project.tools.map(tool => ({
        project_id: projectId,
        tool
      }));
      await supabase.from('project_tools').insert(toolsData);
    }

    // Inserir cores
    if (project.colors?.length) {
      const colorsData = project.colors.map(color => ({
        project_id: projectId,
        color
      }));
      await supabase.from('project_colors').insert(colorsData);
    }

    // Inserir tipografia
    if (project.typography?.length) {
      const typographyData = project.typography.map(font => ({
        project_id: projectId,
        font
      }));
      await supabase.from('project_typography').insert(typographyData);
    }

    // Inserir KPIs
    if (project.kpis?.length) {
      const kpisData = project.kpis.map(kpi => ({
        project_id: projectId,
        label: kpi.label,
        value: kpi.value
      }));
      await supabase.from('project_kpis').insert(kpisData);
    }

    // Inserir imprensa
    if (project.press?.length) {
      const pressData = project.press.map(press => ({
        project_id: projectId,
        outlet: press.outlet,
        url: press.url
      }));
      await supabase.from('project_press').insert(pressData);
    }

    // Inserir galeria
    if (project.gallery?.length) {
      const galleryData = project.gallery.map(image => ({
        project_id: projectId,
        src: image.src,
        alt: image.alt,
        caption: image.caption
      }));
      await supabase.from('project_gallery').insert(galleryData);
    }

    // Inserir before/after
    if (project.beforeAfter) {
      await supabase.from('project_before_after').insert([{
        project_id: projectId,
        before_image: project.beforeAfter.before,
        after_image: project.beforeAfter.after,
        alt_text: project.beforeAfter.alt
      }]);
    }
  },

  async deleteProjectRelations(projectId: string): Promise<void> {
    const tables = [
      'project_disciplines',
      'project_roles',
      'project_tools',
      'project_colors',
      'project_typography',
      'project_kpis',
      'project_press',
      'project_gallery',
      'project_before_after'
    ];

    for (const table of tables) {
      await supabase.from(table).delete().eq('project_id', projectId);
    }
  }
};

// =====================================================
// SERVIÇOS DE DEPOIMENTOS
// =====================================================

export const testimonialsApi = {
  async getAll(): Promise<Testimonial[]> {
    const { data, error } = await supabase
      .from('testimonials')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Testimonial | null> {
    const { data, error } = await supabase
      .from('testimonials')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(testimonial: Omit<Testimonial, 'id'>): Promise<Testimonial> {
    const { data, error } = await supabase
      .from('testimonials')
      .insert([testimonial])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: Partial<Testimonial>): Promise<Testimonial> {
    const { data, error } = await supabase
      .from('testimonials')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('testimonials')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// =====================================================
// SERVIÇOS DE MENSAGENS DE CONTATO
// =====================================================

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  company?: string;
  phone?: string;
  service?: string;
  budget?: string;
  timeline?: string;
  description: string;
  read: boolean;
  created_at: string;
}

export const contactApi = {
  async getAll(): Promise<ContactMessage[]> {
    const { data, error } = await supabase
      .from('contact_messages')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async markAsRead(id: string): Promise<void> {
    const { error } = await supabase
      .from('contact_messages')
      .update({ read: true })
      .eq('id', id);

    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('contact_messages')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async create(message: Omit<ContactMessage, 'id' | 'read' | 'created_at'>): Promise<ContactMessage> {
    const { data, error } = await supabase
      .from('contact_messages')
      .insert([message])
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

// =====================================================
// ANALYTICS
// =====================================================

export interface AnalyticsEvent {
  id: string;
  type: string;
  path: string;
  metadata?: any;
  created_at: string;
}

export const analyticsApi = {
  async track(type: string, path: string, metadata?: any): Promise<void> {
    try {
      await supabase.from('analytics_events').insert([{
        type,
        path,
        metadata
      }]);
    } catch (error) {
      console.error('Error tracking event:', error);
      // Fail silently to not impact user experience
    }
  },

  async getStats(): Promise<{
    pageViews: { current: number; previous: number };
    uniqueVisitors: { current: number; previous: number };
    topPages: { path: string; count: number }[];
    contactSubmissions: { current: number; previous: number };
    portfolioViews: { current: number; previous: number };
  }> {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30)).toISOString();
    const sixtyDaysAgo = new Date(now.setDate(now.getDate() - 30)).toISOString();

    try {
      // 1. Page Views
      const { count: currentViews } = await supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('type', 'page_view')
        .gte('created_at', thirtyDaysAgo);

      const { count: previousViews } = await supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('type', 'page_view')
        .gte('created_at', sixtyDaysAgo)
        .lt('created_at', thirtyDaysAgo);

      // 2. Contact Submissions
      const { count: currentContacts } = await supabase
        .from('contact_messages')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo);

      const { count: previousContacts } = await supabase
        .from('contact_messages')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', sixtyDaysAgo)
        .lt('created_at', thirtyDaysAgo);

      // 3. Portfolio Views (paths starting with /projetos)
      // Note: We need to use like filter, Supabase JS client supports .ilike
      const { count: currentPortfolio } = await supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('type', 'page_view')
        .ilike('path', '/projetos%')
        .gte('created_at', thirtyDaysAgo);

      const { count: previousPortfolio } = await supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('type', 'page_view')
        .ilike('path', '/projetos%')
        .gte('created_at', sixtyDaysAgo)
        .lt('created_at', thirtyDaysAgo);

      return {
        pageViews: { current: currentViews || 0, previous: previousViews || 0 },
        uniqueVisitors: {
          current: Math.floor((currentViews || 0) * 0.7),
          previous: Math.floor((previousViews || 0) * 0.7)
        },
        contactSubmissions: { current: currentContacts || 0, previous: previousContacts || 0 },
        portfolioViews: { current: currentPortfolio || 0, previous: previousPortfolio || 0 },
        topPages: []
      };
    } catch (error) {
      console.error('Error fetching analytics:', error);
      return {
        pageViews: { current: 0, previous: 0 },
        uniqueVisitors: { current: 0, previous: 0 },
        contactSubmissions: { current: 0, previous: 0 },
        portfolioViews: { current: 0, previous: 0 },
        topPages: []
      };
    }
  }
};


// =====================================================
// SERVIÇOS DE AUTENTICAÇÃO
// =====================================================

export const authApi = {
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    return data;
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  },

  async onAuthStateChange(callback: (user: any) => void) {
    return supabase.auth.onAuthStateChange((event, session) => {
      callback(session?.user || null);
    });
  }
};
